import React, { useState, useRef, useEffect } from 'react';
import { IonIcon } from '@ionic/react';
import './AccordionSelect.scss';
import PlusIcon from 'assets/icons/plus.svg';
import MinusIcon from 'assets/icons/minus.svg';
import ArrowIcon from 'assets/icons/arrow-down.svg';

export interface AccordionSelectOption {
  value: string;
  label: string;
}

export interface AccordionSelectSection {
  title: string;
  options: AccordionSelectOption[];
}

export interface AccordionSelectProps {
  sections: AccordionSelectSection[];
  placeholder?: string;
  value?: string;
  onSelectionChange?: (value: string, label: string) => void;
  className?: string;
  disabled?: boolean;
}

const AccordionSelect: React.FC<AccordionSelectProps> = ({
  sections,
  placeholder = 'Select an option',
  value,
  onSelectionChange,
  className = '',
  disabled = false
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set()
  );
  const [selectedValue, setSelectedValue] = useState(value || '');
  const [selectedLabel, setSelectedLabel] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Find the selected label based on the current value
  useEffect(() => {
    if (selectedValue) {
      for (const section of sections) {
        const option = section.options.find(
          (opt) => opt.value === selectedValue
        );
        if (option) {
          setSelectedLabel(option.label);
          break;
        }
      }
    } else {
      setSelectedLabel('');
    }
  }, [selectedValue, sections]);

  // Update internal state when value prop changes
  useEffect(() => {
    setSelectedValue(value || '');
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const toggleDropdown = () => {
    if (!disabled) {
      setIsOpen(!isOpen);
    }
  };

  const toggleSection = (sectionTitle: string) => {
    const newExpandedSections = new Set(expandedSections);
    if (newExpandedSections.has(sectionTitle)) {
      newExpandedSections.delete(sectionTitle);
    } else {
      newExpandedSections.add(sectionTitle);
    }
    setExpandedSections(newExpandedSections);
  };

  const handleOptionSelect = (option: AccordionSelectOption) => {
    setSelectedValue(option.value);
    setSelectedLabel(option.label);
    setIsOpen(false);

    if (onSelectionChange) {
      onSelectionChange(option.value, option.label);
    }
  };

  return (
    <div
      className={`hk-accordion-select ${className} ${
        disabled ? 'disabled' : ''
      }`}
      ref={dropdownRef}
    >
      {/* Main Select Button */}
      <button
        className={`hk-accordion-select-button ${isOpen ? 'open' : ''}`}
        onClick={toggleDropdown}
        disabled={disabled}
        type="button"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            toggleDropdown();
          }
        }}
      >
        <span className="hk-accordion-select-text">
          {selectedLabel || placeholder}
        </span>
        <IonIcon
          icon={ArrowIcon}
          className={`hk-accordion-select-arrow ${isOpen ? 'rotated' : ''}`}
        />
      </button>

      {/* Dropdown Content */}
      {isOpen && (
        <div className="hk-accordion-select-dropdown">
          {sections.map((section, sectionIndex) => (
            <div
              key={section.title || `section-${sectionIndex}`}
              className="hk-accordion-select-section"
            >
              {/* If section has no title, render options directly without accordion */}
              {!section.title ? (
                section.options.map((option) => (
                  <button
                    key={option.value}
                    className={`hk-accordion-select-option hk-accordion-select-top-level-option ${
                      selectedValue === option.value ? 'selected' : ''
                    }`}
                    onClick={() => handleOptionSelect(option)}
                    type="button"
                  >
                    {option.label}
                  </button>
                ))
              ) : (
                <>
                  {/* Section Header */}
                  <button
                    className={`hk-accordion-select-section-header ${
                      expandedSections.has(section.title) ? 'expanded' : ''
                    }`}
                    onClick={() => toggleSection(section.title)}
                    type="button"
                  >
                    <span className="hk-accordion-select-section-title">
                      {section.title}
                    </span>
                    {
                      // Change icon depending on expanded state
                      <IonIcon
                        icon={
                          expandedSections.has(section.title)
                            ? MinusIcon
                            : PlusIcon
                        }
                        className={`hk-accordion-select-section-icon `}
                      />
                    }
                  </button>

                  {/* Section Content */}
                  {expandedSections.has(section.title) && (
                    <div className="hk-accordion-select-section-content">
                      {section.options.map((option) => (
                        <button
                          key={option.value}
                          className={`hk-accordion-select-option ${
                            selectedValue === option.value ? 'selected' : ''
                          }`}
                          onClick={() => handleOptionSelect(option)}
                          type="button"
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default AccordionSelect;
